import React, { FC, useContext, useEffect, useState } from 'react';
import ReccoCard from './recco-card';
import FilterModal from '../filter-modal';
import { Icons, Images } from '../../../../assets';
import { ButtonLoader, Loader } from '../../../../components';
import { AccountContext, AccountContextInterface } from '../../../../contexts';
import { Recco } from '../../../../models';
import { AccountOrganization } from '../../../../models/organization.model';
import { UserSkill } from '../../../../models/skill.model';

interface ReccoSectionProps {
  accountId: string;
  defaultOrganization?: AccountOrganization;
  defaultSkill?: UserSkill;
}

const ReccoSection: FC<ReccoSectionProps> = ({ accountId, defaultOrganization, defaultSkill }) => {
  const { accountService } = useContext<AccountContextInterface>(AccountContext);
  const [selectedOrganizations, setSelectedOrganizations] = useState<AccountOrganization[]>(
    defaultOrganization ? [defaultOrganization] : [],
  );
  const [filterModalOpen, setFilterModalOpen] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [reccos, setReccos] = useState<Recco[]>([]);
  const [page, setPage] = useState<number>(1);
  const [organizationIds, setOrganizationIds] = useState<string[]>(defaultOrganization ? [defaultOrganization.id] : []);
  const [skillIds, setSkillIds] = useState<string[]>(defaultSkill ? [defaultSkill.id] : []);
  const [selectedSkills, setSelectedSkills] = useState<UserSkill[]>(defaultSkill ? [defaultSkill] : []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data } = await accountService.getReccos(accountId, organizationIds, page, 5, skillIds);
        if (data) {
          if (page === 1) {
            setReccos(data);
          } else {
            setReccos(prevReccos => [...prevReccos, ...data]);
          }
          setHasMore(data.length === 5);
        }
      } catch (error) {
        /* empty */
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accountId, accountService, organizationIds, page, skillIds]);

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setPage(prevPage => prevPage + 1);
    }
  };

  const toggleFilterModal = () => {
    setFilterModalOpen(!filterModalOpen);
  };

  const applyFilters = () => {
    const newOrgIds = selectedOrganizations.map(org => org.id);
    const newSkillIds = selectedSkills.map(skill => skill.id);
    setPage(1);
    setOrganizationIds(newOrgIds);
    setSkillIds(newSkillIds);
    toggleFilterModal();
  };

  return (
    <div className="bg-white flex flex-col gap-6 mb-4">
      <div className="flex gap-2 items-center justify-between">
        <div className="flex gap-2 items-center">
          <img src={Icons.RECCO_ICON_V2} alt="reccos" className="h-6 w-6" />
          <span className="font-semibold text-lg text-[#1A1A1A]">Reccos</span>
        </div>
        <div onClick={toggleFilterModal} className="bg-[#F5F5F7] cursor-pointer flex gap-2 items-center p-2 rounded-lg relative">
          <img src={Icons.FILTER} alt="filter" className="h-4 w-4" />
          <span className="font-medium text-xs text-[#1A1A1A]">Filter</span>
          {selectedOrganizations.length + selectedSkills.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">
              {selectedOrganizations.length + selectedSkills.length}
            </span>
          )}
        </div>
      </div>

      {loading && reccos.length === 0 && (
        <div className="flex items-center justify-center">
          <Loader />
        </div>
      )}

      {!loading && reccos.length === 0 && (
        <div className="flex flex-col gap-4 justify-center items-center py-12">
          <img src={Images.NO_ACTIVITY} alt="No Reccos" className="h-48 w-48" />
          <span>No Activity</span>
        </div>
      )}

      {reccos.length > 0 && (
        <div className="flex flex-col gap-6">
          <div className="gap-6 grid grid-cols-1 lg:grid-cols-2">
            {reccos.map(recco => (
              <ReccoCard key={recco.id} recco={recco} />
            ))}
          </div>
          {hasMore && (
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="bg-blue-500 px-4 py-2 rounded-lg text-white disabled:bg-gray-300">
              {loading ? <ButtonLoader /> : 'View More'}
            </button>
          )}
        </div>
      )}

      <FilterModal
        accountId={accountId}
        applyFilters={applyFilters}
        filterModalOpen={filterModalOpen}
        selectedOrganizations={selectedOrganizations}
        selectedSkills={selectedSkills}
        setSelectedOrganizations={setSelectedOrganizations}
        setSelectedSkills={setSelectedSkills}
        toggleFilterModal={toggleFilterModal}
      />
    </div>
  );
};

export default ReccoSection;
